class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  // Added lines: a single-item /cart/add.js answer carries `key`; a multi-item add (`items: [...]`, the build
  // configurators' unit + nested add-on lines) carries `items[].key` and no top-level key. Before 2026-10-16 the
  // multi-item case looked up "cart-notification-product-undefined", threw, and the notification never opened
  // (audit finding 26). Every added line is shown now, add-ons under their unit, with the build and cart totals.
  addedKeys(parsedState) {
    if (parsedState && parsedState.key) return [parsedState.key];
    const items = parsedState && Array.isArray(parsedState.items) ? parsedState.items : [];
    return items.map((item) => item && item.key).filter(Boolean);
  }

  renderContents(parsedState) {
    const keys = this.addedKeys(parsedState);
    const sections = (parsedState && parsedState.sections) || {};
    this.cartItemKey = keys[0];

    const product = document.getElementById('cart-notification-product');
    if (product) {
      try {
        product.innerHTML = this.getProductsHTML(sections['cart-notification-product'], keys);
      } catch (error) {
        console.error('[cart-notification] product render failed:', error);
        product.innerHTML = '';
      }
    }
    ['cart-notification-button', 'cart-icon-bubble'].forEach((id) => {
      const target = document.getElementById(id);
      if (!target || !sections[id]) return;
      try {
        target.innerHTML = this.getSectionInnerHTML(sections[id]);
      } catch (error) {
        console.error(`[cart-notification] ${id} render failed:`, error);
      }
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getProductsHTML(html, keys) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nodes = [];
    const unitKey = keys[0];
    keys.forEach((key, index) => {
      const matches = Array.from(doc.querySelectorAll(`[id="cart-notification-product-${CSS.escape(key)}"]`));
      // An add-on variant can sit under several units with the same key: take the copy under the unit just added.
      const node =
        (index > 0 && matches.find((el) => el.dataset.parentKey === unitKey)) ||
        matches.find((el) => !nodes.includes(el));
      if (node && !nodes.includes(node)) nodes.push(node);
    });

    // "Build total" only for a build: the unit just added plus add-on lines nested under it (parent_relationship).
    // Several unrelated lines in one add are listed flat with no build total (2026-10-16).
    const unitNode = nodes.find((node) => node.id === `cart-notification-product-${unitKey}`);
    const addons = unitNode ? nodes.filter((node) => node.dataset.parentKey === unitKey) : [];
    const build = addons.length > 0;
    const lines = nodes.map((node) => {
      if (addons.includes(node)) node.classList.add('cart-notification-item--addon');
      return node.outerHTML;
    });
    // The lines scroll on their own; the totals below them stay in view next to Check out.
    const parts = [`<div class="cart-notification-product__lines${build ? ' cart-notification-product__lines--build' : ''}">${lines.join('')}</div>`];

    const summary = doc.getElementById('cart-notification-summary');
    if (summary) {
      if (build) {
        const cents = [unitNode].concat(addons).reduce((sum, node) => sum + (parseInt(node.dataset.lineCents, 10) || 0), 0);
        const buildRow = summary.querySelector('[data-build-total]');
        if (buildRow) {
          buildRow.hidden = false;
          buildRow.querySelector('[data-build-total-value]').textContent = this.formatMoney(cents, summary.dataset.currency);
        }
      }
      parts.push(summary.outerHTML);
    }
    return parts.join('');
  }

  formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
